import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';
import { FileText, Loader2, X, AlertTriangle, Search } from 'src/components/common/icons';
import { DateField, Field, formatCurrency, SelectField } from '../Master';
import useCheckNumberValidation from './useCheckNumberValidation';

interface ICreateIRSWithholdingCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    leaseMaster: ILeaseMaster;
}

interface IIRSInvoiceRow {
    lsNo: number;
    invoice: string;
    clNo: number;
    prNo: number;
    amount: number;
    lessor: string;
    tag: number;
}

interface ICompany {
    id: number;
    name: string;
}

const CreateIRSWithholdingCheckModal: React.FC<ICreateIRSWithholdingCheckModalProps> = ({ isOpen, onClose, onSuccess, leaseMaster }) => {
    const { get, post, response } = useFetch();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    // Search Params (Can be null/empty)
    const [searchClNo, setSearchClNo] = useState<string>('');
    const [searchPrNo, setSearchPrNo] = useState<string>('');

    // Check Data
    const [checkNo, setCheckNo] = useState<string>('');
    const [checkDate, setCheckDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [checkTotal, setCheckTotal] = useState<number>(0.0);
    const [compId, setCompId] = useState<string>('');
    const [memo, setMemo] = useState<string>('');

    // Payee Data
    const [payee, setPayee] = useState('');
    const [addr1, setAddr1] = useState('');
    const [addr2, setAddr2] = useState('');
    const [cstz, setCstz] = useState('');

    // Grid & Selection
    const [invoices, setInvoices] = useState<IIRSInvoiceRow[]>([]);
    const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());

    const { isValidating, validationError, isCheckAvailable } = useCheckNumberValidation({
        checkNo,
        compId,
        missingCompanyMessage: 'Select Company First',
        requestFailureMessage: 'Validation Error',
    });

    const [createdCheck, setCreatedCheck] = useState<ILeaseCheck | null>(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            const init = async () => {
                await loadDefaults();
            };
            init();
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const resetForm = () => {
        setInvoices([]);
        setSelectedTags(new Set());
        setCheckNo('');
        setCheckTotal(0);
        setCreatedCheck(null);
        setErrorMsg(null);
        setCompId('');
        // Initialize search fields with current lease context if available, otherwise empty
        setSearchClNo(leaseMaster.clNo ? leaseMaster.clNo.toString() : '');
        setSearchPrNo(leaseMaster.prNo ? leaseMaster.prNo.toString() : '');
    };

    const loadDefaults = async () => {
        setLoading(true);
        try {
            // 1. Get Companies
            const comps = await get('/v1.0/leases/master/companies');
            if (isMounted.current && response.ok) {
                if (Array.isArray(comps)) {
                    setCompanies(comps);
                    if (comps.length > 0) {
                        setCompId(comps[0].id.toString());
                    }
                }
            }

            // 2. Get Payee Defaults (usp_PopulateIRSCheck)
            const defaults = await get('/v1.0/leases/master/irs-check-defaults');
            if (isMounted.current && response.ok && defaults) {
                setPayee(defaults.payee || '');
                setAddr1(defaults.addr1 || '');
                setAddr2(defaults.addr2 || '');
                setCstz(defaults.cstz || '');
                if (defaults.checkDate) {
                    setCheckDate(defaults.checkDate.split('T')[0]);
                }
            }
        } catch (e) {
            // ignore
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleQuery = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            let url = `/v1.0/leases/master/irs-check-invoices?`;
            if (searchClNo) {
                url += `clNo=${searchClNo}&`;
            }
            if (searchPrNo) {
                url += `prNo=${searchPrNo}`;
            }

            const data = await get(url);
            if (isMounted.current) {
                if (response.ok) {
                    setInvoices(data || []);
                    setSelectedTags(new Set()); // Clear selection on new query
                } else {
                    setErrorMsg('Failed to load invoices.');
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error while querying.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // Calculate Total based on selected rows
    useEffect(() => {
        let total = 0;
        invoices.forEach((inv) => {
            if (selectedTags.has(inv.tag)) {
                total += inv.amount || 0;
            }
        });
        setCheckTotal(total);
    }, [selectedTags, invoices]);

    // Grid Handlers
    const toggleSelectAll = () => {
        if (selectedTags.size === invoices.length && invoices.length > 0) {
            setSelectedTags(new Set());
        } else {
            const allTags = new Set(invoices.map((x) => x.tag));
            setSelectedTags(allTags);
        }
    };

    const toggleRow = (tag: number) => {
        const next = new Set(selectedTags);
        if (next.has(tag)) {
            next.delete(tag);
        } else {
            next.add(tag);
        }
        setSelectedTags(next);
    };

    const handleCreate = async () => {
        if (!checkNo || parseInt(checkNo, 10) <= 0) {
            return;
        }
        if (checkTotal <= 0) {
            return;
        }
        if (selectedTags.size === 0) {
            return;
        }
        if (!isCheckAvailable) {
            return;
        }

        setLoading(true);
        try {
            // PAYLOAD MATCHING C# DTO
            const payload = {
                CheckNo: parseInt(checkNo, 10),
                CheckDate: checkDate,
                CheckAmount: checkTotal,
                CompID: parseInt(compId, 10),
                TagList: JSON.stringify(Array.from(selectedTags)), // Matches input.TagList
                Payee: payee,
                Addr1: addr1,
                Addr2: addr2,
                CSTZ: cstz,
                MemoFor: memo,
                // Note: ClNo/PrNo typically strictly for query, not passed to Process SP unless explicitly needed for logging
            };

            await post('/v1.0/leases/master/process-irs-check', payload);

            if (!isMounted.current) {
                return;
            }

            if (response.ok) {
                onSuccess?.();
                const checkDetails = await get(`/v1.0/leases/master/checks/${payload.CheckNo}?compId=${payload.CompID}`);
                if (response.ok && checkDetails && checkDetails.length > 0) {
                    setCreatedCheck(checkDetails[0]);
                } else {
                    onClose();
                }
            } else {
                setErrorMsg(getLeaseRecordsApiErrorMessage(response.data, response.status));
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg(getLeaseRecordsApiErrorMessage(undefined, 500));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const renderCheckNoLabel = () => (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>Check No.</span>
            <span style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600 }}>
                {isValidating && (
                    <>
                        <Loader2 style={styles.spinner} />
                        <span style={{ color: '#6B7280' }}>Checking...</span>
                    </>
                )}
                {!isValidating && validationError && <span style={styles.errorText}>{validationError}</span>}
                {!isValidating && isCheckAvailable && <span style={styles.successText}>Available</span>}
            </span>
        </span>
    );

    const isCreateDisabled = loading || !compId || !isCheckAvailable || selectedTags.size === 0 || checkTotal <= 0;

    if (!isOpen) {
        return null;
    }

    if (createdCheck) {
        return <CheckDetailsModal check={createdCheck} onClose={onClose} />;
    }

    // Duplicate Select Logic Fix
    const companyOptions = companies.map((c) => ({ value: c.id.toString(), label: c.name }));
    if (!compId) {
        companyOptions.unshift({ value: '', label: 'Select...' });
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        IRS Withholding Check
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {errorMsg && (
                        <div style={styles.errorState} role="alert">
                            <AlertTriangle style={styles.errorIcon} />
                            {errorMsg}
                        </div>
                    )}

                    <div style={styles.formContainer}>
                        {/* LEFT COLUMN: Check Info & Query Params */}
                        <div style={styles.col}>
                            <Field label={renderCheckNoLabel() as unknown as string} value={checkNo} onChange={setCheckNo} editable={!loading} />

                            <DateField label="Check Date" value={checkDate} onChange={setCheckDate} editable={!loading} />

                            <Field label="Check Total" value={checkTotal.toFixed(2)} prefix="$" editable={false} />

                            <SelectField
                                label="Company (Required)"
                                value={compId}
                                onChange={setCompId}
                                editable={!loading}
                                options={companyOptions}
                            />

                            {/* QUERY ROW (Bottom Left as per screenshot) */}
                            <div style={styles.queryRow}>
                                <div style={{ flex: 1 }}>
                                    <Field label="Client No." value={searchClNo} onChange={setSearchClNo} editable={!loading} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Field label="Project No." value={searchPrNo} onChange={setSearchPrNo} editable={!loading} />
                                </div>
                                <button onClick={handleQuery} style={styles.queryBtn} disabled={loading}>
                                    {loading && <Loader2 style={styles.spinner} />}
                                    {!loading && <Search style={{ width: '14px' }} />}
                                    <span style={{ marginLeft: '4px' }}>Query</span>
                                </button>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Payee Info */}
                        <div style={styles.col}>
                            <Field label="Payee" value={payee} onChange={setPayee} editable={!loading} />
                            <Field label="Address" value={addr1} onChange={setAddr1} editable={!loading} />
                            <Field label="Address 2" value={addr2} onChange={setAddr2} editable={!loading} />
                            <Field label="City, ST, Zip" value={cstz} onChange={setCstz} editable={!loading} />

                            <div style={{ marginTop: '10px' }}>
                                <Field label="Memo" value={memo} onChange={setMemo} editable={!loading} />
                            </div>
                        </div>
                    </div>

                    {/* GRID SECTION */}
                    <div style={styles.tableWrapper}>
                        <table style={styles.tableBase}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={{ ...styles.tableHeaderCell, width: '40px', textAlign: 'center' }}>
                                        <LeaseRecordInput
                                            type="checkbox"
                                            checked={invoices.length > 0 && selectedTags.size === invoices.length}
                                            onChange={toggleSelectAll}
                                            disabled={loading}
                                        />
                                    </th>
                                    <th style={styles.tableHeaderCell}>Lease No</th>
                                    <th style={styles.tableHeaderCell}>Invoice No</th>
                                    <th style={styles.tableHeaderCell}>Cl No</th>
                                    <th style={styles.tableHeaderCell}>Pr No</th>
                                    <th style={styles.tableHeaderCell}>Withholding Amt.</th>
                                    <th style={styles.tableHeaderCell}>Lessor Name</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
                                            No records found. Enter Client/Project and click Query.
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((inv) => (
                                        <tr key={inv.tag} style={styles.tableRow}>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                                                <LeaseRecordInput
                                                    type="checkbox"
                                                    checked={selectedTags.has(inv.tag)}
                                                    onChange={() => toggleRow(inv.tag)}
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td style={styles.tableDataCell}>{inv.lsNo}</td>
                                            <td style={styles.tableDataCell}>{inv.invoice}</td>
                                            <td style={styles.tableDataCell}>{inv.clNo}</td>
                                            <td style={styles.tableDataCell}>{inv.prNo}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.amount)}</td>
                                            <td style={styles.tableDataCell}>{inv.lessor}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    <button onClick={toggleSelectAll} style={{ ...styles.buttonSecondary, marginRight: 'auto' }}>
                        Select All
                    </button>
                    <button onClick={() => setSelectedTags(new Set())} style={{ ...styles.buttonSecondary }}>
                        Deselect All
                    </button>

                    <div style={{ flex: 1 }} />

                    <button onClick={onClose} style={{ ...styles.buttonSecondary, marginRight: 'auto' }} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        style={{
                            ...styles.buttonPrimary,
                            opacity: isCreateDisabled ? 0.5 : 1,
                            cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                            marginRight: 'auto',
                        }}
                        disabled={isCreateDisabled}
                    >
                        {loading ? 'Processing...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Styles (Identical to other modals) ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '95%',
        maxWidth: '1100px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' },
    formContainer: { display: 'flex', gap: '2rem', marginBottom: '1.5rem' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
    queryRow: { display: 'flex', alignItems: 'flex-end', gap: '10px', marginTop: 'auto' }, // Pushed to bottom
    queryBtn: {
        padding: '0 1rem',
        height: '38px',
        borderRadius: '0.375rem',
        backgroundColor: '#F3F4F6',
        border: '1px solid #D1D5DB',
        cursor: 'pointer',
        fontWeight: 500,
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    select: {
        padding: '0.4rem 0.8rem',
        borderRadius: '0.375rem',
        border: '1px solid #D1D5DB',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
    },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, borderRadius: '0.5rem', border: '1px solid #E5E7EB', minHeight: '200px' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 10 },
    tableHeaderCell: {
        padding: '0.75rem 0.5rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease' },
    tableDataCell: { padding: '0.5rem', fontSize: '0.875rem', color: '#111827', borderBottom: '1px solid #F3F4F6' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#6B7280', marginRight: '4px' },
    errorText: { color: '#EF4444' },
    successText: { color: '#10B981' },
};

export default CreateIRSWithholdingCheckModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/CreateIRSCheckModal.tsx